import React from 'react'

import { useLockBodyScroll } from '../../../hooks'
import { Backdrop } from '../../Backdrop'
import { Button, ButtonProps } from '../../Button'
import { Link, type LinkProps } from '../../Link'
import { Body, Container, Inner, Title } from './styles'

export interface DialogProps {
  /**
   * Dialog open state
   */
  open?: boolean

  /**
   * Dialog title
   */
  title: React.ReactNode

  /**
   * Dialog body
   */
  body?: React.ReactNode

  /**
   * Positive action button config
   */
  positiveAction?: {
    label?: string
    href?: string
  } & Pick<LinkProps, 'as' | 'external' | 'target' | 'rel'> &
    Omit<ButtonProps, 'variant' | 'size'>

  /**
   * Negative action button config
   */
  negativeAction?:
    | true
    | ({
        label?: string
        href?: string
      } & Pick<LinkProps, 'as' | 'external' | 'target' | 'rel'> &
        Omit<ButtonProps, 'variant' | 'size'>)

  /**
   * Disable close on backdrop click
   */
  disableBackdropClose?: boolean

  /**
   * Backdrop opacity
   */
  backdropOpacity?: number

  /**
   * Callback triggered on dialog close
   */
  onClose?: () => void

  /**
   * Callback triggered when dialog is completely closed
   */
  onCloseEnd?: () => void
}

export const Dialog = ({
  open,
  title,
  body,
  positiveAction,
  negativeAction,
  disableBackdropClose,
  backdropOpacity,
  onClose,
  onCloseEnd,
}: DialogProps) => {
  useLockBodyScroll(open)

  const handlePositiveClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      positiveAction?.onClick?.(e)
      onClose?.()
    },
    [positiveAction, onClose],
  )

  const handleNegativeClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (typeof negativeAction === 'object') {
        negativeAction?.onClick?.(e)
      }
      onClose?.()
    },
    [negativeAction, onClose],
  )

  return (
    <Backdrop
      open={open}
      opacity={backdropOpacity}
      // Above modals
      zIndex={200}
      onClick={!disableBackdropClose ? onClose : undefined}
      onClosed={onCloseEnd}
    >
      <Container open={open}>
        <Inner>
          <Title>{title}</Title>

          {body && <Body>{body}</Body>}

          <div className="mt-auto flex w-full items-center justify-end pt-2">
            {negativeAction === true && (
              <Button variant="primary-text" onClick={handleNegativeClick}>
                キャンセル
              </Button>
            )}

            {typeof negativeAction === 'object' && (
              <Button
                variant="primary-text"
                {...negativeAction}
                onClick={handleNegativeClick}
                asChild={negativeAction.href ? true : undefined}
              >
                {negativeAction.href ? (
                  <Link
                    href={negativeAction.href}
                    as={negativeAction.as}
                    target={negativeAction.target}
                    rel={negativeAction.rel}
                    external={negativeAction.external}
                  >
                    {negativeAction.label}
                  </Link>
                ) : (
                  negativeAction.label
                )}
              </Button>
            )}

            <Button
              variant="brand-text"
              {...positiveAction}
              onClick={handlePositiveClick}
              asChild={positiveAction?.href ? true : undefined}
            >
              {positiveAction?.href ? (
                <Link
                  href={positiveAction.href}
                  as={positiveAction.as}
                  target={positiveAction.target}
                  rel={positiveAction.rel}
                  external={positiveAction.external}
                >
                  {positiveAction.label ?? 'OK'}
                </Link>
              ) : (
                positiveAction?.label ?? 'OK'
              )}
            </Button>
          </div>
        </Inner>
      </Container>
    </Backdrop>
  )
}
